import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PLAN_LIMITS } from '@/lib/config'
import type { Profile, Site } from '@/types/database'

interface TopUser {
  id: string
  email: string
  events_count: number
  sessions_count: number
  percentage_of_limit: number
}

interface TopSite {
  id: string
  domain: string
  user_email: string
  user_id: string
  events_count: number
  rage_click_sessions: number
}

export default function AdminAbusePage() {
  const [topUsers, setTopUsers] = useState<TopUser[]>([])
  const [topSites, setTopSites] = useState<TopSite[]>([])
  const [overLimitUsers, setOverLimitUsers] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchAbuseData()
  }, [])

  const fetchAbuseData = async () => {
    setIsLoading(true)

    try {
      // Top users by event count
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .order('events_count', { ascending: false })
        .limit(10) as { data: Profile[] | null }

      if (profiles) {
        setTopUsers(
          profiles.map((p) => ({
            id: p.id,
            email: p.email,
            events_count: p.events_count,
            sessions_count: p.sessions_count,
            percentage_of_limit: Math.round((p.events_count / PLAN_LIMITS.eventsPerMonth) * 100),
          }))
        )

        // Users over limits
        setOverLimitUsers(
          profiles.filter(
            (p) =>
              p.events_count > PLAN_LIMITS.eventsPerMonth ||
              p.sessions_count > PLAN_LIMITS.sessionsPerMonth
          )
        )
      }

      // Top sites by activity
      const { data: sites } = await supabase
        .from('sites')
        .select('*, profiles!inner(email)') as { data: (Site & { profiles: { email: string } })[] | null }

      if (sites) {
        // Get event counts and rage click info for each site
        const sitesWithStats = await Promise.all(
          sites.map(async (site) => {
            const { count: eventsCount } = await supabase
              .from('events')
              .select('*', { count: 'exact', head: true })
              .eq('site_id', site.id)

            const { count: rageClickSessions } = await supabase
              .from('sessions')
              .select('*', { count: 'exact', head: true })
              .eq('site_id', site.id)
              .eq('has_rage_clicks', true)

            return {
              id: site.id,
              domain: site.domain,
              user_email: (site.profiles as { email: string }).email,
              user_id: site.user_id,
              events_count: eventsCount || 0,
              rage_click_sessions: rageClickSessions || 0,
            }
          })
        )

        setTopSites(
          sitesWithStats
            .sort((a, b) => b.events_count - a.events_count)
            .slice(0, 10)
        )
      }
    } catch (error) {
      console.error('Failed to fetch abuse data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Abuse Detection</h1>

      {/* Over limit warning */}
      {overLimitUsers.length > 0 && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
          <p className="text-red-300 font-medium">
            {overLimitUsers.length} user(s) have exceeded their usage limits
          </p>
        </div>
      )}

      {/* Top users by event volume */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Top 10 Users by Event Volume</h2>
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium text-right">Events</th>
                <th className="px-4 py-3 font-medium text-right">Sessions</th>
                <th className="px-4 py-3 font-medium text-right">% of Limit</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user) => (
                <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-white">{user.email}</td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {user.events_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {user.sessions_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`badge ${
                        user.percentage_of_limit > 100
                          ? 'badge-danger'
                          : user.percentage_of_limit > 80
                          ? 'badge-warning'
                          : 'badge-gray'
                      }`}
                    >
                      {user.percentage_of_limit}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/users/${user.id}`}
                      className="text-primary-400 hover:text-primary-300"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top sites */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Top 10 Sites by Events</h2>
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="px-4 py-3 font-medium">Domain</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium text-right">Events</th>
                <th className="px-4 py-3 font-medium text-right">Rage Click Sessions</th>
              </tr>
            </thead>
            <tbody>
              {topSites.map((site) => (
                <tr key={site.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-white">{site.domain}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/users/${site.user_id}`}
                      className="text-primary-400 hover:text-primary-300"
                    >
                      {site.user_email}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {site.events_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {site.rage_click_sessions > 0 ? (
                      <span className="badge-danger">{site.rage_click_sessions}</span>
                    ) : (
                      <span className="text-gray-500">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Abuse flags */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4">Potential Issues</h2>
        <div className="bg-gray-800 rounded-xl p-6 space-y-4">
          {topUsers.some((u) => u.percentage_of_limit > 100) ? (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-red-400 font-medium">Users exceeding limits</p>
                <p className="text-gray-400 text-sm">
                  {topUsers.filter((u) => u.percentage_of_limit > 100).length} user(s) have exceeded their monthly event limit
                </p>
              </div>
            </div>
          ) : null}

          {topSites.some((s) => s.rage_click_sessions > 10) ? (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-yellow-400 font-medium">High rage click rate</p>
                <p className="text-gray-400 text-sm">
                  Some sites have many sessions with rage clicks - may indicate UX issues or bot activity
                </p>
              </div>
            </div>
          ) : null}

          {!topUsers.some((u) => u.percentage_of_limit > 100) &&
            !topSites.some((s) => s.rage_click_sessions > 10) && (
              <div className="text-center py-4">
                <p className="text-green-400 font-medium">No issues detected</p>
                <p className="text-gray-400 text-sm">All users and sites are within normal parameters</p>
              </div>
            )}
        </div>
      </section>
    </div>
  )
}
